// PhotoStep.tsx — steps 'photo-choice' / 'photo-select' / 'photo-upload'.
// Qué hace: selección unificada de imágenes de fondo: las del scan + subidas +
//   URLs pegadas, todo en una pantalla.
// Qué exporta: PhotoStep.
// Quién lo usa: CreateWorkspaceModal.

import { RefObject } from 'react';
import { ClassifiedImage } from '@agency-smm/shared';
import { Button } from '../../Button';
import { Input } from '../../Input';
import { HiddenInput, ImgGrid, ImgItem, Badge } from '../styled';

interface Props {
  allImages: ClassifiedImage[];
  selectedLogo: string | null;
  selectedPhotos: Set<string>;
  setSelectedPhotos: React.Dispatch<React.SetStateAction<Set<string>>>;
  setUploadedPhotos: React.Dispatch<React.SetStateAction<File[]>>;
  photoFileRef: RefObject<HTMLInputElement>;
  photoUrlInput: string;
  setPhotoUrlInput: (v: string) => void;
  addPhotoUrl: () => void;
  confirmPhotos: () => void;
}

export function PhotoStep({
  allImages, selectedLogo, selectedPhotos, setSelectedPhotos, setUploadedPhotos,
  photoFileRef, photoUrlInput, setPhotoUrlInput, addPhotoUrl, confirmPhotos,
}: Props) {
  const scanPhotos = allImages.filter(i => i.url !== selectedLogo);
  return (
    <>
      <HiddenInput
        ref={photoFileRef}
        type="file"
        accept="image/*"
        multiple
        onChange={(e) => {
          const files = Array.from(e.target.files || []);
          if (files.length > 0) {
            setUploadedPhotos(prev => [...prev, ...files]);
            files.forEach(f => {
              const blobUrl = URL.createObjectURL(f);
              setSelectedPhotos(prev => new Set([...prev, blobUrl]));
            });
          }
          e.target.value = '';
        }}
      />

      {/* Scan images */}
      {scanPhotos.length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 4 }}>
            From your website ({scanPhotos.length})
          </div>
          <ImgGrid>
            {scanPhotos.map((img, i) => (
              <ImgItem
                key={`scan-${i}`}
                $selected={selectedPhotos.has(img.url)}
                onClick={() => {
                  setSelectedPhotos(prev => {
                    const next = new Set(prev);
                    next.has(img.url) ? next.delete(img.url) : next.add(img.url);
                    return next;
                  });
                }}
              >
                <img src={img.url} alt={`Scan ${i + 1}`} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                {selectedPhotos.has(img.url) && <Badge>✓</Badge>}
              </ImgItem>
            ))}
          </ImgGrid>
        </>
      )}

      {/* Uploaded / pasted images */}
      {[...selectedPhotos].filter(u => !scanPhotos.some(s => s.url === u)).length > 0 && (
        <>
          <div style={{ fontSize: 11, fontWeight: 700, color: '#64748B', textTransform: 'uppercase', letterSpacing: '0.5px', marginTop: 8, marginBottom: 4 }}>
            Your uploads ({[...selectedPhotos].filter(u => !scanPhotos.some(s => s.url === u)).length})
          </div>
          <ImgGrid>
            {[...selectedPhotos].filter(u => !scanPhotos.some(s => s.url === u)).map((url, i) => (
              <ImgItem key={`upload-${i}`} $selected>
                <img src={url} alt={`Upload ${i + 1}`} onError={e => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                <Badge>✓</Badge>
              </ImgItem>
            ))}
          </ImgGrid>
        </>
      )}

      {/* Add more: URL input + upload button */}
      <div style={{ display: 'flex', gap: 6, marginTop: 8, marginBottom: 8 }}>
        <Input
          value={photoUrlInput}
          onChange={e => setPhotoUrlInput(e.target.value)}
          placeholder="Paste image URL..."
          onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); addPhotoUrl(); } }}
          style={{ flex: 1 }}
        />
        <Button type="button" $size="sm" onClick={addPhotoUrl} disabled={!photoUrlInput.trim()}>Add</Button>
      </div>
      <div style={{ display: 'flex', gap: 6 }}>
        <Button type="button" $variant="outline" $size="sm" onClick={() => photoFileRef.current?.click()} style={{ flex: 1 }}>
          Upload Files
        </Button>
        <Button type="button" $size="sm" onClick={confirmPhotos} style={{ flex: 1 }}>
          {selectedPhotos.size > 0 ? `Continue with ${selectedPhotos.size} image${selectedPhotos.size > 1 ? 's' : ''}` : 'Skip'}
        </Button>
      </div>
    </>
  );
}
